/* eslint-disable i18next/no-literal-string */
import {Button} from '@dropbox/dig-components/buttons';
import {Skeleton} from '@dropbox/dig-components/skeleton';
import {SystemBanner} from '@dropbox/dig-components/system_banner';
import {Text} from '@dropbox/dig-components/typography';
import {Stack} from '@dropbox/dig-foundations';
import {useQuery} from '@tanstack/react-query';
import {pulseUserAtom} from 'atoms/auth';
import {AdminAskHealthService} from 'client';
import {Breadcrumb} from 'components/DSYS/Breadcrumb';
import {Layout} from 'components/DSYS/Layout';
import {Title} from 'components/DSYS/Title';
import {ROUTE_PATHS} from 'constant';
import {isSuperUser} from 'helpers/utils';
import {useDocumentTitle} from 'hooks/useDocumentTitle';
import {useAtomValue} from 'jotai';
import {type CSSProperties} from 'react';
import {useNavigate} from 'react-router';
import {getService} from 'utilities';
import styles from 'views/admin/ask_health/AskHealth.module.css';
import {AskHealthReportView} from 'views/admin/ask_health/AskHealthReportView';

const WEEKS = 12;
// The full-width container already pads the pane, so the inset adds no gutter,
// and the column may lean all the way to the middle: it's centered, and the
// wide tables break out evenly on both sides.
const CENTERED_COLUMN = {
  '--dsys-page-gutter': '0px',
  '--dsys-content-max-offset': '100vw',
} as CSSProperties;

export const AdminAskHealth = () => {
  const pulseUser = useAtomValue(pulseUserAtom);
  const navigate = useNavigate();
  const canView = !!pulseUser && isSuperUser(pulseUser);

  useDocumentTitle('Ask health');

  const {data, isError, isLoading, isFetching, refetch} = useQuery({
    queryKey: ['admin', 'ask-health', WEEKS],
    queryFn: () => getService(AdminAskHealthService).getAskHealthApiV1AdminAskHealthGet(WEEKS),
    enabled: canView,
    staleTime: 5 * 60 * 1000,
  });

  if (!pulseUser) {
    return null;
  }

  if (!canView) {
    navigate('/');
    return null;
  }

  return (
    // Full width with a centered reading column, so the wide tables can break
    // out past it like open workstream cards do.
    <Layout.Container width="100%">
      <Layout.MaxWidth
        width="reading"
        inset
        lean
        breakout
        className="meticulous-ignore"
        style={CENTERED_COLUMN}
      >
        <Breadcrumb
          path={[
            {children: 'Admin', to: ROUTE_PATHS.ADMIN},
            {children: 'Ask health', to: ROUTE_PATHS.ADMIN_ASK_HEALTH},
          ]}
        />
        <Stack gap="24">
          <div className={styles.header}>
            <Stack gap="4">
              <Title size={24}>Ask health</Title>
              <Text color="subtle">
                Is Ask getting better? Answer quality, answer rate, speed and Dash use on live
                traffic, by week.
              </Text>
            </Stack>
            <Button variant="outline" onClick={() => refetch()} isLoading={isFetching}>
              Refresh
            </Button>
          </div>

          {isError ? (
            <SystemBanner tone="alert">
              <Text>Couldn&apos;t read Braintrust. Try again in a minute.</Text>
            </SystemBanner>
          ) : null}

          {isLoading ? (
            <Stack gap="12">
              <Skeleton.Box height={96} />
              <Skeleton.Box height={200} />
              <Skeleton.Box height={200} />
            </Stack>
          ) : data ? (
            <AskHealthReportView report={data} />
          ) : null}
        </Stack>
      </Layout.MaxWidth>
    </Layout.Container>
  );
};
